import { useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState, type EmptyStateProps } from '@/components/EmptyState';
import { cn } from '@/lib/utils';
import { filterRows, paginate, sortRows, type SortDir } from '@/lib/tableLogic';

export interface Column<T> {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  sortValue?: (row: T) => string | number | null | undefined;
  className?: string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  /** Must be stable and unique per row (an id), never an array index: rows are re-sorted and filtered. */
  getRowKey: (row: T) => string | number;
  searchText?: (row: T) => string | undefined;
  searchPlaceholder?: string;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  empty?: Pick<EmptyStateProps, 'title' | 'description' | 'action' | 'icon'>;
  /**
   * Convenience for pointer users. The primary cell should still contain a real
   * link/button for the same action so keyboard and assistive tech users have it too.
   * Clicks on interactive descendants (a, button, input, ...) are ignored; add
   * `data-row-ignore` to any other element that should not trigger the row action.
   */
  onRowClick?: (row: T) => void;
  pageSize?: number;
  toolbar?: ReactNode;
  /** When this value changes (e.g. an external filter), the table returns to page 1. */
  pageResetKey?: unknown;
}

const INTERACTIVE = 'a,button,input,select,textarea,label,[role=menuitem],[data-row-ignore]';

/**
 * Use for list pages: searchable, sortable, paginated table with loading,
 * error and empty states. The header sticks within a 70vh scroll area.
 */
export function DataTable<T>({
  columns, rows, getRowKey, searchText, searchPlaceholder = 'Search…', loading, error, onRetry,
  empty = { title: 'Nothing here yet' }, onRowClick, pageSize = 20, toolbar, pageResetKey,
}: DataTableProps<T>) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(null);
  const [page, setPage] = useState(1);
  const [prevResetKey, setPrevResetKey] = useState(pageResetKey);
  if (!Object.is(prevResetKey, pageResetKey)) {
    setPrevResetKey(pageResetKey);
    setPage(1);
  }

  let data = rows ?? [];
  if (searchText) data = filterRows(data, query, searchText);
  const sortCol = columns.find((c) => c.key === sort?.key);
  if (sortCol?.sortValue && sort) data = sortRows(data, sortCol.sortValue, sort.dir);
  const view = paginate(data, page, pageSize);

  const hasRows = !!rows && rows.length > 0;
  const retry = onRetry && <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>;

  if (error && !hasRows) {
    return (
      <div role="alert">
        <EmptyState title="Couldn't load data" description={error} action={retry} />
      </div>
    );
  }
  if ((loading || !rows) && !hasRows && !error) {
    return <div className="flex justify-center py-14"><Spinner /></div>;
  }

  const toggleSort = (key: string) => {
    setSort((s) => (s?.key === key ? (s.dir === 'asc' ? { key, dir: 'desc' } : null) : { key, dir: 'asc' }));
    setPage(1);
  };

  const rowClick = (row: T) => (e: MouseEvent<HTMLTableRowElement>) => {
    if (!e.currentTarget.contains(e.target as Node)) return; // portaled React children
    if ((e.target as HTMLElement).closest(INTERACTIVE)) return;
    onRowClick?.(row);
  };

  const rowKeyDown = (row: T) => (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      if (e.key === ' ') e.preventDefault();
      onRowClick?.(row);
    }
  };

  const q = query.trim();
  const noMatch = view.items.length === 0 && q !== '' && hasRows;

  return (
    <div className="space-y-3">
      {(searchText || toolbar) && (
        <div className="flex flex-wrap items-center gap-2">
          {searchText && (
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" aria-hidden />
              <Input type="search" value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                placeholder={searchPlaceholder} className="pl-8" aria-label="Search" />
            </div>
          )}
          {toolbar}
        </div>
      )}
      {error && (
        <div role="alert" className="flex items-center justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm">
          <span>{error}</span>
          {retry}
        </div>
      )}
      {noMatch ? (
        <EmptyState title="No results" description={`Nothing matches "${q}".`}
          action={<Button variant="ghost" size="sm" onClick={() => { setQuery(''); setPage(1); }}>Clear search</Button>} />
      ) : view.items.length === 0 ? (
        <EmptyState {...empty} />
      ) : (
        <div aria-busy={loading || undefined}
          className={cn('max-h-[70vh] overflow-auto rounded-lg border bg-card', loading && 'opacity-60 transition-opacity')}>
          <Table containerClassName="overflow-visible">
            <TableHeader>
              <TableRow>
                {columns.map((c) => {
                  const sorted = sort?.key === c.key ? sort.dir : null;
                  return (
                    <TableHead key={c.key} className={cn('sticky top-0 z-10 bg-card shadow-[inset_0_-1px_0_var(--border)]', c.className)}
                      aria-sort={sorted ? (sorted === 'asc' ? 'ascending' : 'descending') : undefined}>
                      {c.sortValue ? (
                        <button type="button"
                          className="inline-flex items-center gap-1 rounded-sm outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={() => toggleSort(c.key)}>
                          {c.header}
                          {sorted && (sorted === 'asc' ? <ArrowUp className="size-3" aria-hidden /> : <ArrowDown className="size-3" aria-hidden />)}
                        </button>
                      ) : c.header}
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {view.items.map((row) => (
                <TableRow key={getRowKey(row)}
                  onClick={onRowClick && rowClick(row)}
                  onKeyDown={onRowClick && rowKeyDown(row)}
                  tabIndex={onRowClick ? 0 : undefined}
                  className={cn('h-12', onRowClick && 'cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring')}>
                  {columns.map((c) => <TableCell key={c.key} className={c.className}>{c.cell(row)}</TableCell>)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {view.pageCount > 1 && (
        <nav aria-label="Pagination" className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
          <span aria-live="polite">Page {view.page} of {view.pageCount}</span>
          <Button variant="outline" size="sm" aria-label="Previous page" disabled={view.page <= 1} onClick={() => setPage(view.page - 1)}>Previous</Button>
          <Button variant="outline" size="sm" aria-label="Next page" disabled={view.page >= view.pageCount} onClick={() => setPage(view.page + 1)}>Next</Button>
        </nav>
      )}
    </div>
  );
}
